import type { Recipe, SuggestedActivity } from '@domain/models/suggested-activities'
import type { RecommendedActivityTile } from '@presentation/hooks/useRecommendedActivities'

interface RecommendedActivityCardProps {
  tile: RecommendedActivityTile
  onClick?: (tile: RecommendedActivityTile) => void
}

const PLACEHOLDER_IMAGE = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300" fill="%23E5E7EB"%3E%3Crect width="400" height="300"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%239CA3AF" font-size="18"%3ENo Image%3C/text%3E%3C/svg%3E'

function getCardImage(content: SuggestedActivity | Recipe | null): string {
  if (!content) {
    return PLACEHOLDER_IMAGE
  }
  return content.imageUrl || PLACEHOLDER_IMAGE
}

function getCardTitle(
  tile: RecommendedActivityTile,
  content: SuggestedActivity | Recipe | null,
): string {
  if (!content) {
    return `${tile.category.title} library`
  }
  return content.title
}

function getCardLength(content: SuggestedActivity | Recipe | null): string | null {
  if (!content || !content.length) {
    return null
  }
  return content.length
}

export default function RecommendedActivityCard({
  tile,
  onClick,
}: RecommendedActivityCardProps) {
  const title = getCardTitle(tile, tile.content)
  const image = getCardImage(tile.content)
  const length = getCardLength(tile.content)

  return (
    <button
      type="button"
      onClick={() => onClick?.(tile)}
      className="group flex w-full items-start gap-3 overflow-hidden rounded-xl bg-card-surface p-3 text-left no-underline shadow-lg shadow-black/20 ring-1 ring-black/5 transition-[transform,box-shadow] duration-200 hover:scale-[1.01] hover:shadow-lg hover:shadow-black/30 focus-visible:scale-[1.01]"
      aria-label={`${tile.category.title} recommended activity`}
    >
      <div className="flex min-h-24 min-w-0 flex-1 flex-col sm:min-h-28">
        <div>
          <span className="mb-1.5 inline-block rounded-full bg-blue-light px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-blue">
            {tile.category.title}
          </span>
          <h3 className="line-clamp-4 min-h-16 text-sm font-bold leading-tight text-text-primary group-hover:text-primary lg:text-base">
            {title}
          </h3>
        </div>
        {length && (
          <div className="mt-auto shrink-0 pt-2 text-xs font-semibold text-text-muted">
            {length}
          </div>
        )}
      </div>
      <div className="relative size-24 shrink-0 overflow-hidden rounded-lg bg-border-light sm:size-28">
        <img
          src={image}
          alt={title}
          className="absolute inset-0 block h-full w-full object-cover"
          loading="lazy"
        />
      </div>
    </button>
  )
}
